import { isNetworkError, isOnline } from '../offline/network';

const store = new Map();
const inflight = new Map();

/** Default list TTL — keep data warm across admin page switches */
const DEFAULT_TTL = 5 * 60_000;
/** How long past TTL we still return stale data while refreshing */
const STALE_GRACE_MS = 10 * 60_000;
const KV_PREFIX = 'qc:';

let persistTimer = null;
let persistQueued = new Set();

function persistLater(key) {
  if (typeof window === 'undefined') return;
  persistQueued.add(key);
  if (persistTimer) return;
  persistTimer = window.setTimeout(() => {
    persistTimer = null;
    const keys = [...persistQueued];
    persistQueued = new Set();
    import('../offline/offlineDb')
      .then(({ setKv, deleteKv }) => {
        keys.forEach((cacheKey) => {
          const entry = store.get(cacheKey);
          if (!entry) {
            deleteKv(`${KV_PREFIX}${cacheKey}`).catch(() => {});
            return;
          }
          setKv(`${KV_PREFIX}${cacheKey}`, entry).catch(() => {});
        });
      })
      .catch(() => {});
  }, 160);
}

export function peekCache(key, { allowStale = true } = {}) {
  const entry = store.get(key);
  if (!entry) return null;
  const now = Date.now();
  if (now <= entry.expires) return entry.value;
  if (allowStale && (now <= entry.staleUntil || !isOnline())) return entry.value;
  if (!isOnline()) return entry.value;
  store.delete(key);
  persistLater(key);
  return null;
}

/** Strict fresh-only peek (no stale). */
export function peekFreshCache(key) {
  return peekCache(key, { allowStale: false });
}

export function writeCache(key, value, ttlMs = DEFAULT_TTL) {
  const now = Date.now();
  store.set(key, {
    value,
    expires: now + ttlMs,
    staleUntil: now + ttlMs + STALE_GRACE_MS,
  });
  persistLater(key);
}

export function invalidateCache(prefix = '') {
  if (!prefix) {
    store.clear();
    inflight.clear();
    import('../offline/offlineDb').then(({ deleteKvPrefix }) => deleteKvPrefix(KV_PREFIX)).catch(() => {});
    return;
  }

  [...store.keys()].forEach((key) => {
    if (key.startsWith(prefix)) {
      store.delete(key);
      persistLater(key);
    }
  });
  [...inflight.keys()].forEach((key) => {
    if (key.startsWith(prefix)) inflight.delete(key);
  });
}

/**
 * Cache with stale-while-revalidate:
 * - Fresh hit → return immediately
 * - Stale hit → return immediately and refresh in background
 * - Miss → await network (deduped)
 * - Offline / network error → last snapshot (even past grace)
 */
export async function cachedQuery(key, fetcher, ttlMs = DEFAULT_TTL) {
  const entry = store.get(key);
  const now = Date.now();

  if (entry && now <= entry.expires) {
    return entry.value;
  }

  const refresh = () => {
    if (inflight.has(key)) return inflight.get(key);

    const promise = Promise.resolve()
      .then(fetcher)
      .then((value) => {
        writeCache(key, value, ttlMs);
        inflight.delete(key);
        return value;
      })
      .catch((error) => {
        inflight.delete(key);
        if (entry && (isNetworkError(error) || !isOnline())) {
          return entry.value;
        }
        throw error;
      });

    inflight.set(key, promise);
    return promise;
  };

  if (entry && (now <= entry.staleUntil || !isOnline())) {
    if (isOnline()) refresh().catch(() => {});
    return entry.value;
  }

  if (!isOnline() && entry) {
    return entry.value;
  }

  try {
    return await refresh();
  } catch (error) {
    if (entry && (isNetworkError(error) || !isOnline())) return entry.value;
    throw error;
  }
}

export async function hydrateQueryCache() {
  try {
    const { getKvPrefix } = await import('../offline/offlineDb');
    const rows = await getKvPrefix(KV_PREFIX);
    rows.forEach((row) => {
      const key = String(row.key || '').slice(KV_PREFIX.length);
      if (key && row.value && typeof row.value === 'object') {
        store.set(key, row.value);
      }
    });
  } catch {
    /* first visit / private mode */
  }
}
